import { ApolloClient } from "@apollo/client";
import { ConfigGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/ConfigGraphQlAdapter";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import {
  CREATE_CONFIG_PORTAL_CREDENTIAL,
  DELETE_CONFIG_PORTAL_CREDENTIAL,
  GET_CONFIG_PORTAL_CREDENTIALS,
  GET_PURCHASER_PORTAL_KEYS_BY_KEY,
  UPDATE_CONFIG_PORTAL_CREDENTIAL,
} from "@src/domain/graphql/config";
import {
  ConfigPortalCredential,
  type IConfigPortalCredential,
} from "@src/domain/entities/config";
import {
  PurchaserPortalKey,
  type IPurchaserPortalKey,
} from "@src/domain/entities/purchaser";
import type {
  CreateConfigPortalCredentialDTO,
  DeleteConfigPortalCredentialDTO,
  GetConfigPortalCredentialsDTO,
  UpdateConfigPortalCredentialDTO,
} from "@src/domain/dto/config";
import type {
  ConfigPortalCredentialsListResult,
  IConfigPortalCredentialRepository,
} from "@src/domain/repositories/config";
import {
  requireConfigMutationData,
  throwIfConfigGraphQLError,
  throwIfConfigMutationError,
  toConfigListResult,
} from "./configRepository.shared";

function mapCredentialItem(item: IConfigPortalCredential): ConfigPortalCredential {
  return ConfigPortalCredential.from({
    id: item.id,
    purchaserId: item.purchaserId,
    purchaserName: item.purchaserName,
    purchaserKey: item.purchaserKey,
    dataProviderTypeId: item.dataProviderTypeId ?? null,
    portalUserName: item.portalUserName,
    portalPassword: item.portalPassword ?? null,
    portalKey: item.portalKey ?? null,
    isActive: item.isActive,
    metadata: item.metadata ?? null,
  });
}

export class ConfigPortalCredentialRepository
  implements IConfigPortalCredentialRepository
{
  private readonly tenantClient: ApolloClient;
  private readonly catalogClient: ApolloClient;

  constructor(
    tenantClient: ApolloClient = ConfigGraphQlAdapter.getInstance().getClient(),
    catalogClient: ApolloClient = GraphQlAdapter.getInstance().getClient(),
  ) {
    this.tenantClient = tenantClient;
    this.catalogClient = catalogClient;
  }

  async getPortalCredentials(
    params: GetConfigPortalCredentialsDTO,
  ): Promise<ConfigPortalCredentialsListResult> {
    const { data, error } = await this.tenantClient.query<{
      configPortalCredentials: {
        items: IConfigPortalCredential[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_CONFIG_PORTAL_CREDENTIALS,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfConfigGraphQLError({ error });

    const response = data?.configPortalCredentials;
    if (!response) {
      throw new Error("Failed to fetch portal credentials");
    }

    return toConfigListResult(
      response,
      mapCredentialItem,
      params.page_number,
      params.page_size,
    );
  }

  async createPortalCredential(
    params: CreateConfigPortalCredentialDTO,
  ): Promise<ConfigPortalCredential> {
    const result = await this.tenantClient.mutate<{
      createConfigPortalCredential: IConfigPortalCredential;
    }>({
      mutation: CREATE_CONFIG_PORTAL_CREDENTIAL,
      variables: {
        params: {
          purchaserId: params.purchaserId,
          portalKey: params.portalKey,
          portalUserName: params.portalUserName,
          portalPassword: params.portalPassword,
          isActive: params.isActive,
          metadata: params.metadata,
        },
      },
      errorPolicy: "all",
    });

    throwIfConfigMutationError(result.error);
    const payload = requireConfigMutationData(
      result.data?.createConfigPortalCredential,
      result.error,
      "Failed to create portal credential",
    );
    return mapCredentialItem(payload);
  }

  async updatePortalCredential(
    params: UpdateConfigPortalCredentialDTO,
  ): Promise<ConfigPortalCredential> {
    const result = await this.tenantClient.mutate<{
      updateConfigPortalCredential: IConfigPortalCredential;
    }>({
      mutation: UPDATE_CONFIG_PORTAL_CREDENTIAL,
      variables: {
        params: {
          id: params.id,
          portalUserName: params.portalUserName,
          portalPassword: params.portalPassword,
          isActive: params.isActive,
          metadata: params.metadata,
        },
      },
      errorPolicy: "all",
    });

    throwIfConfigMutationError(result.error);
    const payload = requireConfigMutationData(
      result.data?.updateConfigPortalCredential,
      result.error,
      "Failed to update portal credential",
    );
    return mapCredentialItem(payload);
  }

  async deletePortalCredential(
    params: DeleteConfigPortalCredentialDTO,
  ): Promise<{ id: number; message: string | null }> {
    const result = await this.tenantClient.mutate<{
      deleteConfigPortalCredential: { id: number; message?: string | null };
    }>({
      mutation: DELETE_CONFIG_PORTAL_CREDENTIAL,
      variables: {
        params: { id: params.id },
      },
      errorPolicy: "all",
    });

    throwIfConfigMutationError(result.error);
    const payload = requireConfigMutationData(
      result.data?.deleteConfigPortalCredential,
      result.error,
      "Failed to delete portal credential",
    );
    return {
      id: payload.id,
      message: payload.message ?? null,
    };
  }

  async getPortalKeysByPurchaserKey(
    purchaserKey: string,
  ): Promise<PurchaserPortalKey[]> {
    const { data, error } = await this.catalogClient.query<{
      purchaserPortalKeys: IPurchaserPortalKey[] | null;
    }>({
      query: GET_PURCHASER_PORTAL_KEYS_BY_KEY,
      variables: { purchaserKey },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    if (error instanceof Error && error.message.trim()) {
      throw error;
    }

    return (data?.purchaserPortalKeys ?? []).map((item) =>
      PurchaserPortalKey.from(item),
    );
  }
}
